import { Divider, Select as AntSelect } from 'antd';
import styled from 'styled-components';
import { setIconColor } from 'modules/utils/cssUtils';
import { Flex } from '../flex';
import { theme } from '../theme';
import { NavigationAndActionInfoSmall as TooltipIcon } from '../icons';

export interface StyledSelectProps {
  disabled?: boolean;
  readOnly?: boolean;
  $hasError?: boolean;
  multiple?: boolean;
}

export const StyledSelect = styled(AntSelect)<StyledSelectProps>`
  color: ${({theme}) => theme.colors.primaryK9};
  border: 1px solid ${theme.colors.primaryK4};
  width: 100%;
  transition: all 0.15s linear;
  padding: 0.25rem;

  font-weight: ${theme.fontWeight.thin};
  font-size: var(--main-font-size);
  line-height: var(--main-line-height);

  .ant-select-selection-placeholder {
    color: ${({theme}) => theme.colors.primaryK9};
  }

  &:focus {
    outline: none;
  }

  &:hover,
  &:active,
  &:focus {
    border: 1px solid ${({theme}) => theme.colors.primaryK10};
  }

  .ant-select-dropdown {
    padding: 0;
    border: 1px solid ${({theme}) => theme.colors.primaryK10};
    border-radius: 0;
    left: -0.313rem !important;
    background: ${({theme}) => theme.colors.primaryW};
  }
  .ant-select-selector {
    padding: 0 4px !important;

    .ant-select-selection-search {
      left: 4px;
    }
  }
  &.ant-select-multiple,
  &.ant-select-single {
    background-color: ${({theme}) => theme.colors.primaryW};
  }

  ${(props) =>
    props.disabled
      ? `
          &, .ant-select-selector {
            cursor: default !important;
          }
          &, &:hover, &:active, &:focus {
            color: ${props.theme.colors.primaryK3};
            background-color: ${props.theme.colors.primaryK1};
            border: 1px solid ${props.theme.colors.primaryK3};
          }
          .ant-select-selection-placeholder {
            color: ${props.theme.colors.primaryK3};
          }
          .ant-select-selector {
            background-color: ${props.theme.colors.primaryK1} !important;
            input {
              cursor: default !important;
              pointer-events: none;
            }
          }
          .ant-select-selection-item {
            color: ${props.theme.colors.primaryK3};
            background-color: ${props.theme.colors.primaryK1} !important;
          }
          &.ant-select-multiple, &.ant-select-single {
            background-color: ${props.theme.colors.primaryK1};
          }
          `
      : ''};

  ${(props) =>
    props.readOnly
      ? `
          .ant-select-selector {
            background-color: ${props.theme.colors.primaryK1} !important;
            input {
              cursor: default !important;
              pointer-events: none;
            }
            .ant-select-selection-item {
              background-color: ${props.theme.colors.primaryK1};
            }
          }
          &.ant-select-multiple, &.ant-select-single {
            background-color: $props.{theme.colors.primaryK1};
          }
          &, &:hover, &:active, &:focus {
            color: ${props.theme.colors.primaryK9};
            background-color: ${props.theme.colors.primaryK1};
            border: 1px solid ${props.theme.colors.primaryK3};
            cursor: default;
            pointer-events: none;
      
            .ant-select-selector {
              background: none;
            }
          }
         .ant-select-selection-placeholder {
            color: ${props.theme.colors.primaryK10};
          }
          `
      : ''};

  ${(props) =>
    props.$hasError
      ? `
      &, &:hover, &:active, &:focus { 
        border: 2px solid ${props.theme.colors.primaryR5}; 
      }
      .ant-select-dropdown {
        left: -0.375rem !important;
        border-top: 2px solid ${props.theme.colors.primaryR5}; 
      }
      `
      : ''};

  &.ant-select:not(.ant-select-customize-input) .ant-select-selector {
    border: none;
  }

  &.ant-select-show-search:not(.ant-select-customize-input)
    .ant-select-selector {
    cursor: pointer;
  }

  .ant-select-item {
    min-height: 2.5rem;
    align-items: center;
    color: ${({theme}) => theme.colors.primaryK10};
    border: none;
    width: 100%;
    transition: all 0.15s linear;

    div div svg path {
      fill: none;
    }

    font-weight: ${theme.fontWeight.thin};
    font-size: var(--main-font-size);
    line-height: var(--main-line-height);

    &:hover {
      background-color: ${({theme}) => theme.colors.primaryT2};
      color: ${({theme}) => theme.colors.primaryW};

      .ant-select-item-option-content {
        div {
          ${(props) =>
            props.multiple
              ? `
          border: 1px solid ${props.theme.colors.primaryW};`
              : `
          border: none;
          `}
        }
      }
    }

    &.ant-select-item-option-selected {
      background-color: ${({theme}) => theme.colors.primaryW};
      &:hover {
        background-color: ${({theme}) => theme.colors.primaryT2};
        color: ${({theme}) => theme.colors.primaryW};
        div div svg path {
          fill: ${({theme}) => theme.colors.primaryW};
        }
      }
      div div svg path {
        fill: ${({theme}) => theme.colors.primaryT2};
      }
    }
  }

  .ant-select-selection-item {
    border: none;
    background-color: white;
    align-items: center;
    padding: 0;
    &:after {
      content: ';';
    }
    div {
      display: none;
    }

    .ant-select-selection-item-remove {
      display: none;
    }
  }

  .ant-select-selection-item-content {
    padding: 0;
    margin: 0;
  }

  .ant-select-item-option-content {
    white-space: normal;
    width: 19rem;
    max-width: 19rem;
    height: auto;
  }

  .ant-select-selection-overflow {
    justify-content: start;
    justify-items: flex-start;
    flex-wrap: nowrap;
    max-width: 18rem;
    white-space: nowrap;
    overflow: clip;
    text-overflow: ellipsis;
    direction: ltr;
  }

  .ant-select-selector {
    box-shadow: none !important;
  }

  &.ant-select-multiple .ant-select-show-search {
    border: none;
    .ant-select-selector {
      border: none;
    }
  }

  .ant-select .ant-select-selection-overflow-item {
    svg {
      display: none;
    }
    border: none;
    background-color: none;
  }

  .ant-select-item-option-state {
    display: none;
    padding: 0.313rem;
    border: solid ${({theme}) => theme.colors.primaryK10} 1px;
    border-radius: 4px;

    svg path {
      display: none;
    }
  }
`;

export const SelectWrapper = styled(Flex)`
  width: 100%;
`;

export const OptionAll = styled.div<{
  selected?: boolean;
  multiple?: boolean;
}>`
  padding: 0.5rem 2.265rem 0.5rem 0.625rem;
  cursor: pointer;
  color: ${({theme}) => theme.colors.primaryK10};
  border: none;
  width: 100%;
  transition: all 0.15s linear;

  font-weight: ${theme.fontWeight.thin};
  font-size: var(--main-font-size);
  line-height: var(--main-line-height);

  div div svg path {
    fill: none;
  }

  &:hover {
    background-color: ${({theme}) => theme.colors.primaryT2};
    color: ${({theme}) => theme.colors.primaryW};

    div {
      border: 1px solid ${({theme}) => theme.colors.primaryW};
    }
  }

  ${(props) =>
    props.selected
      ? `
        svg path {
          fill: ${props.theme.colors.primaryT2};
        }
        &:hover {
          svg path {
            fill: ${props.theme.colors.primaryW};
          }
      }
        `
      : `
      svg path {
        fill: none;
        }`}

  ${(props) => (props.multiple ? `` : `display: none;`)}
`;

export const StyledDivider = styled(Divider)<{
  multiple?: boolean;
}>`
  background-color: ${({theme}) => theme.colors.primaryK2};
  margin: 0;

  ${(props) => (props.multiple ? `` : `display: none;`)}
`;

export const SelectArrowFlex = styled(Flex)<{
  disabled?: boolean;
  readOnly?: boolean;
}>`
  width: 1rem;
  height: 0.625rem;
  position: relative;
  top: -2.5rem;
  align-self: flex-end;
  user-select: none;
  cursor: pointer;
  margin: 1rem 1rem 0 0;
  pointer-events: none;

  svg {
    width: 1rem;
    height: 0.625rem;
  }

  ${(props) =>
    props.disabled || props.readOnly
      ? `
        pointer-events: none;
        svg path {
          fill: ${props.theme.colors.primaryK3};
        }`
      : ''}
`;

export const SelectTickFlex = styled(Flex)<{
  selected?: boolean;
  multiple?: boolean;
}>`
  height: 1.375rem;
  width: 1.375rem;
  padding: 0.313rem;
  border: solid ${({theme}) => theme.colors.primaryK10} 1px;
  border-radius: 4px;
  position: absolute;
  right: 0.625rem;
  top: 0.5rem;
  cursor: pointer;

  ${(props) =>
    props.multiple
      ? ``
      : `
    border: none;
    &:hover {
      border: none;
    }
    `}
`;

export const NumberOfSelectedFlex = styled(Flex)<{
  number?: number;
}>`
  position: absolute;
  align-self: flex-end;
  height: 1.5rem;
  width: 1rem;
  font-weight: ${theme.fontWeight.bold};
  font-size: var(--h5-font-size);
  line-height: var(--h4-line-height);
  display: flex;
  flex: none;
  order: 0;
  flex-grow: 0;
  margin-right: 1.8rem;
  margin-top: 0.5rem;
  cursor: pointer;
  pointer-events: none;
`;

export const SelectError = styled.div<{
  hasError?: boolean;
}>`
  width: 100%;
  & > p {
    background-color: ${({theme}) => theme.colors.primaryR5};
    color: ${({theme}) => theme.colors.primaryW};
    padding: 0.563rem 0.625rem;
    user-select: none;

    font-weight: ${theme.fontWeight.thin};
    font-size: var(--main-font-size);
    line-height: var(--main-line-height);
    margin: 0;
    transition: all 0.5s linear;
    opacity: 0;
    height: 0;
  }

  opacity: 0;
  height: 0;
  margin-top: -1.625rem;
  transition: all 0.15s linear;

  ${(props) =>
    props.hasError
      ? `
    opacity:1; 
    height: 100%;
    
    & > p {
      opacity:1; 
      height: 100%;
    }
  `
      : ''};
`;

export const SelectLabel = styled.label<{
  disabled?: boolean;
  readOnly?: boolean;
  filled?: boolean;
}>`
  color: ${(props) =>
    props.filled
      ? ` ${props.theme.colors.primaryK7}`
      : ` ${props.theme.colors.primaryK10}`};
  font-weight: ${theme.fontWeight.thin};
  font-size: var(--main-font-size);
  line-height: var(--main-line-height);
  margin-bottom: 0.125rem;
  user-select: none;

  ${(props) =>
    props.disabled || props.readOnly
      ? `
          color: ${props.theme.colors.primaryK7};
        `
      : ''}
`;

export const StyledSelectWithLabel = styled(Flex)<{
  filled?: boolean;
}>`
  margin-bottom: 0rem;
  width: 100%;

  :hover label {
    ${(props) =>
      props.filled
        ? `
          color: ${props.theme.colors.primaryK10};
        `
        : ''}
  }

  :focus-within label {
    color: ${({theme}) => theme.colors.primaryK7};
  }
`;

export const StyledTooltipIcon = styled(TooltipIcon)`
  height: 1.125rem;
  width: 1.125rem;
  transition: all 0.15s linear;
  fill: ${({theme}) => theme.colors.primaryK9};

  &:hover {
    ${({theme}) => setIconColor(theme.colors.primaryT2)};
  }
`;

export const IconsWrapper = styled(Flex)`
  position: relative;
  margin-right: 2.5rem;
  top: -1.3rem;
  height: 0;

  flex-direction: row;
  align-self: flex-end;
  align-items: center;
`;
